"use client";

import { Icon } from "@/components/shared/icon";
import { Button } from "@/components/ui/button";
import {
  faExclamationTriangle,
  faHome,
  faRefresh,
} from "@fortawesome/pro-solid-svg-icons";
import Link from "next/link";
import { useEffect } from "react";
import { usePostHog } from "posthog-js/react";

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function GlobalError({ error, reset }: ErrorProps) {
  const posthog = usePostHog();

  useEffect(() => {
    // Log the error to console (existing)
    console.error("Application server error:", error);

    // Send error to PostHog
    posthog?.capture("application_server_error", {
      error_message: error.message,
      error_stack: error.stack,
      error_digest: error.digest,
      error_name: error.name,
      url: window.location.href,
      user_agent: navigator.userAgent,
    });
  }, [error, posthog]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-100 text-center">
      <div className="p-12 max-w-2xl">
        <Icon
          icon={faExclamationTriangle}
          size="6x"
          className="mb-8 text-amber-500 animate-pulse"
        />

        <h1 className="text-6xl font-bold text-gray-800">500</h1>

        <h2 className="mt-4 text-2xl font-semibold text-gray-600">
          Oops! Something went wrong.
        </h2>

        <p className="mt-4 text-gray-500 leading-relaxed">
          We're experiencing some technical difficulties. Our team has been
          notified and is working to fix the issue.
        </p>

        {error.digest && (
          <div className="mt-4 p-3 bg-gray-200 rounded-lg">
            <p className="text-sm text-gray-600 font-mono">
              Error ID: {error.digest}
            </p>
          </div>
        )}

        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={reset}
            variant="default"
            size="lg"
            className="flex items-center gap-2"
          >
            <Icon icon={faRefresh} size="sm" />
            Try Again
          </Button>

          <Button
            asChild
            variant="outline"
            size="lg"
            className="flex items-center gap-2"
          >
            <Link href="/">
              <Icon icon={faHome} size="sm" />
              Go Home
            </Link>
          </Button>
        </div>

        <div className="mt-8 text-sm text-gray-400">
          <p>If the problem persists, please contact our support team.</p>
        </div>
      </div>
    </div>
  );
}
